import { AccountantService, ClientService } from "../../../../services";
import {
	BackdropCircularProgress,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
} from "@websential/cosmic-v20";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatAmountCommaSeperator,
	formatDate,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount
} from "../../../../common/Common";
import {
	setSelectedAccountant,
	setSelectedAccountantName,
	setSelectedActivityLogActions,
	setSelectedClient,
	setSelectedClientName,
	setSelectedStickyNavBar,
} from "../../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { EmployeeService } from "../../../../services/EmployeeService";
import { IAppState } from "../../../../store/reducers";
import { IDropDownListResponse } from "../../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../../models";
import { RollBackTransactionsDataGrid } from "./RollBackTransactionsDataGrid";
import { RollBackTransactionsHeader } from "./RollBackTransactionsHeader"
import { RollBackTransactionsToolbar } from "./RollBackTransactionsToolbar/RollBackTransactionsToolbar";
import { TransactionsServices } from "../../../../services/DirectDeposit/TransactionsServices";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { setTransactionsHeaders } from "../../../../store/actions/transactionsHeadersActions";
import { useNavigate } from "react-router-dom";

export interface IRollBackTransactionsSetupPage { }
const ROLL_BACK_TRANSACTIONS_QUERY_KEY = "roll-back-transactions";
const CLIENT_DDL_DATA_QUERY_KEY = "client-ddl-data-record";
const ACCOUNTANT_DDL_DATA_QUERY_KEY = "accountant-ddl-data-record";

const useRollBackTransactions = (
	runGetListAPICall: boolean,
	accountant: string,
	client: string,
	clientType: string | number,
	// type: string,
	employee: string,
	transactionMethod: string | number,
	dateAppliesOn: string | number,
	startDate: string | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			ROLL_BACK_TRANSACTIONS_QUERY_KEY,
			runGetListAPICall,
			accountant,
			client,
			clientType,
			// type,
			employee,
			transactionMethod,
			dateAppliesOn,
			params,
		],
		queryFn: () =>
			TransactionsServices.getDirectDepositRollBackTransactionsList(
				runGetListAPICall,
				accountant,
				client,
				clientType,
				// type,
				employee,
				transactionMethod,
				dateAppliesOn,
				startDate,
				endDate,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useGetAccountantDDLItemsItems = () =>
	useQuery({
		queryKey: [ACCOUNTANT_DDL_DATA_QUERY_KEY],
		queryFn: (data) => {
			return AccountantService.getGetAccountantDDL();
		},
		refetchOnWindowFocus: false,
	});
const useGetClientsDDLItems = (accountantId: string) =>
	useQuery({
		queryKey: [CLIENT_DDL_DATA_QUERY_KEY, accountantId],
		queryFn: (data) => {
			return ClientService.getGetClientDDL(accountantId);
		},
		refetchOnWindowFocus: false,
	});
const useGetEmployeeDDLItems = (clientId: string) =>
	useQuery({
		queryKey: [CLIENT_DDL_DATA_QUERY_KEY, clientId],
		queryFn: (data) => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});


export const RollBackTransactions: React.FC<IRollBackTransactionsSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openWireTransferWarningDialoage, setOpenWireTransferWarningDialoage] = useState(false);
	const [openWireTransferWarningDialoageData, setOpenWireTransferWarningDialoageData] = useState<any>(null);
	const [openWireTransferWarningDialoageDataForSave, setOpenWireTransferWarningDialoageDataForSave] = useState<any>(null);
	const [buttonTag, setButtonTag] = useState<any>(null);
	const [dialogButtonLabel, setDialogButtonLabel] = useState("");
	const [dialogContent, setDialogContent] = useState("");

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedRows, setSelectedRows] = useState([]);
	const [selectedSubRows, setSelectedSubRows] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [accountantId, setAccountantId] = useState("all");
	const [clientId, setClientId] = useState("all");
	const [rollBackTransactionsData, setRollBackTransactionsData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	let {
		accountantStored,
		clientStored,
		clientTypeStored,
		typeStored,
		transactionMethodStored,
		dateAppliesOnStored,
		startDateStored,
		endDateStored,
	} = useSelector((state: IAppState) => {
		return {
			accountantStored: state.transactionsHeaders.accountant,
			clientStored: state.transactionsHeaders.client,
			clientTypeStored: state.transactionsHeaders.clientType,
			typeStored: state.transactionsHeaders.type,
			transactionMethodStored: state.transactionsHeaders.transactionMethod,
			dateAppliesOnStored: state.transactionsHeaders.dateAppliesOn,
			startDateStored: state.transactionsHeaders.startDate,
			endDateStored: state.transactionsHeaders.endDate,
		};
	});
	const transactionsHeaders = useSelector((state: IAppState) => state.transactionsHeaders);




	const accountant = formMethods.getValues("accountant");
	const client = formMethods.getValues("client");
	const clientType = formMethods.getValues("clientType");
	// const type = formMethods.getValues("type");
	const employee = formMethods.getValues("employee");
	const transactionMethod = formMethods.getValues("transactionMethod");
	const dateAppliesOn = formMethods.getValues("dateAppliesOn");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");

	useEffect(() => {
		if (accountantStored && accountantStored !== accountant) {
			formMethods.setValue('accountant', accountantStored)
		}
		if (clientStored && clientStored !== client) {
			formMethods.setValue('client', clientStored)
		}
		if (clientTypeStored && clientTypeStored !== clientType) {
			formMethods.setValue('clientType', clientTypeStored)
		}
		// if (typeStored && typeStored !== type) {
		// 	formMethods.setValue('type', typeStored)
		// }
		if (dateAppliesOnStored && dateAppliesOnStored !== dateAppliesOn) {
			formMethods.setValue('dateAppliesOn', dateAppliesOnStored)
		}
		if (transactionMethodStored && transactionMethodStored !== transactionMethod) {
			formMethods.setValue('transactionMethod', transactionMethodStored)
		}
		if (startDateStored && startDateStored !== startDate) {
			let date = new Date(startDateStored)
			formMethods.setValue('startDate', date)
		}
		if (endDateStored && endDateStored !== endDate) {
			let date = new Date(endDateStored)
			formMethods.setValue('endDate', date)
		}
		// formMethods.setValue('accountant', accountantStored)
		// formMethods.setValue('client', clientStored)
		// formMethods.setValue('clientType', clientTypeStored)
		// formMethods.setValue('type', typeStored)
		// formMethods.setValue('dateAppliesOn', dateAppliesOnStored)
		// formMethods.setValue('startDate', startDateStored)
		// formMethods.setValue('endDate', endDateStored)
	}, [accountantStored, clientStored, clientTypeStored, typeStored, transactionMethodStored, dateAppliesOnStored, startDateStored, endDateStored])






	// const { isPending, data } = useRollBackTransactions(
	// 	runGetListAPICall,
	// 	accountant && accountant,
	// 	client && client,
	// 	clientType && clientType,
	// 	type && type,
	// 	dateAppliesOn && dateAppliesOn,
	// 	formatDate(startDate),
	// 	formatDate(endDate),
	// 	queryParams,
	// 	navigateToLogout
	// );
	const { isPending, data } = useRollBackTransactions(
		runGetListAPICall,
		accountantStored && accountantStored,
		clientStored && clientStored,
		clientTypeStored && clientTypeStored,
		employee && employee,
		transactionMethodStored && transactionMethodStored,
		dateAppliesOnStored && dateAppliesOnStored,
		startDateStored && startDateStored,
		endDateStored && endDateStored,
		queryParams,
		navigateToLogout
	);
	const RollBackTransactionsList: any | null = data;

	useEffect(() => {
		if (RollBackTransactionsList) {
			setRollBackTransactionsData(RollBackTransactionsList);
		}
	}, [RollBackTransactionsList]);

	const { isPending: isProcessing, mutate: confirmRollBackTransactions } =
		useMutation({
			mutationFn: TransactionsServices.updateDirectDepositForCollections,
			onSuccess: (res: IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					// let path = `/company/departments/setup/${res.data.id}`;
					// navigate(path);
					// return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
					// }),
					// queryClient.invalidateQueries({
					// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					// }),
					// ]);
					queryClient.invalidateQueries({ queryKey: [ROLL_BACK_TRANSACTIONS_QUERY_KEY] })
				} else if (res.status === 201) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					// let path = `/company/departments/setup/${res.data.id}`;
					// navigate(path);
					// setEditRecordId(res.data.id);
					// return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
					// }),
					// queryClient.invalidateQueries({
					// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					// }),
					// ]);
					queryClient.invalidateQueries({ queryKey: [ROLL_BACK_TRANSACTIONS_QUERY_KEY] })
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingAccountantDDLItemsItems, data: accountantDDL } =
		useGetAccountantDDLItemsItems();
	const { isPending: isPendingClientsDDLItems, data: clientsDDL } =
		useGetClientsDDLItems(accountantId);
	const { isPending: isPendingEmployeeDDLItems, data: employeeDDL } =
		useGetEmployeeDDLItems(clientId);

	//   after click apply button api calls with updated values and update employee report data
	// const RollBackTransacitonsDataAPICall = (
	// 	clientIdStore: string,
	// ) => {
	// 	const fetchData = async () => {
	// 		setIsLoading(true);
	// 		setQueryParams({
	// 			search_query: "",
	// 			order_by: "firstName",
	// 			order_direction: "asc",
	// 			page: 1,
	// 			size: 25,
	// 		});
	// 		try {
	// 			const response: any = await ReportsService.getRollBackTransacitons(
	// 				clientIdStore,
	// 				{
	// 					search_query: "",
	// 					order_by: "firstName",
	// 					order_direction: "asc",
	// 					page: 1,
	// 					size: 25,
	// 				},
	// 				navigateToLogout
	// 			);

	// 			if (response) {
	// 				setRollBackTransactionsData(response);
	// 			}
	// 		} catch {
	// 		} finally {
	// 			setIsLoading(false);
	// 		}
	// 	};
	// 	fetchData();
	// };


	const accountantDDLItems = useMemo(() => {
		if (accountantDDL) {
			if (
				accountantDDL &&
				Array.isArray(accountantDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(accountantDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [accountantDDL]);

	const clientsDDLItems = useMemo(() => {
		if (clientsDDL) {
			if (
				clientsDDL &&
				Array.isArray(clientsDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(clientsDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [clientsDDL]);
	const employeeDDLItems = useMemo(() => {
		if (employeeDDL) {
			if (
				employeeDDL &&
				Array.isArray(employeeDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(employeeDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDL]);

	const onAccountantSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false)
		setAccountantId(event.value)
		dispatch(setTransactionsHeaders({ accountant: event.value, client: "all" }));
		formMethods.setValue('client', 'all')
	}
	const onClientSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false)
		setClientId(event.value)
		dispatch(setTransactionsHeaders({ client: event.value }));
		formMethods.setValue('employee', 'all')
	}

	//   after click apply button api calls with updated values and update roll back transaction
	const rollBackTransactionsDataAPICall = (
		accountant: string,
		client: string,
		clientType: string,
		// type: string,
		employee: string,
		transactionMethod: string | number,
		dateAppliesOn: string,
		startDate: string | null,
		endDate: string | null,
	) => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const response: any = await TransactionsServices.getDirectDepositRollBackTransactionsList(
					true,
					// 900,
					accountant,
					client,
					clientType,
					// type,
					employee,
					transactionMethod,
					dateAppliesOn,
					formatDate(startDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setRollBackTransactionsData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onClickHandler = async (clickedRow: any) => {
		setIsLoading(true);
		try {
			const accountantDetails = await AccountantService.getById(
				clickedRow.accountantId,
				navigateToLogout
			);

			if (accountantDetails && accountantDetails.id) {
				dispatch(setSelectedAccountant({ id: accountantDetails.id }));
				dispatch(setSelectedAccountant(accountantDetails));
				dispatch(setSelectedAccountantName({ accTitle: accountantDetails.accTitle }));

				const clientDetails = await ClientService.getById(clickedRow.clientId, navigateToLogout);

				if (clientDetails && clientDetails.id) {
					dispatch(
						setSelectedClient({
							...clientDetails,
							superUserFieldsShownInClientSetup: true,
						})
					);
					dispatch(setSelectedClientName({ clientName: clientDetails.legalName }));
					navigate("/clients/dashboard");
				}
			} else {
				const response = await ClientService.getById(clickedRow.clientId, navigateToLogout);

				if (response && response.id) {
					dispatch(setSelectedClient(response));
					navigate("/clients/dashboard");
				}
			}
		} catch (error) {
			// handle/log error as needed
		} finally {
			setIsLoading(false);
		}
	};

	const onSubmit = (values: any) => {
		rollBackTransactionsDataAPICall(
			values.accountant,
			values.client,
			values.clientType,
			// values.type,
			values.employee,
			values.transactionMethod,
			values.dateAppliesOn,
			values.startDate,
			values.endDate,
		);
	};

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const updatedRollBackTransactionsListItems = useMemo(() => {
		return (
			rollBackTransactionsData &&
			rollBackTransactionsData.items &&
			rollBackTransactionsData?.items.map((item: any) => ({
				...item,
				clientName: (
					<span
						// to={`/clients/dashboard`}
						className="no-underline"
						style={{ color: theme.palette.primary.light, cursor: "pointer" }}
						onClick={() => onClickHandler(item)}
					>
						{item.clientName}
					</span>
				),
				orders: item.orders &&
					item.orders.map((item: any) => ({
						...item,
						clientName: (
							<span
								// to={`/clients/dashboard`}
								className="no-underline"
								style={{ color: theme.palette.primary.light, cursor: "pointer" }}
								onClick={() => onClickHandler(item)}
							>
								{item.clientName}
							</span>
						),
						total: formatAmountCommaSeperator(item.total)

					})),
				total: formatAmountCommaSeperator(item.total)

			}))
		);
	}, [rollBackTransactionsData]);



	// const updatedTotalSelectedRows = (rows: any, subRows: any) => {
	// 	const rowsWhichParentNotSelected = rows && rows.reduce((acc: any, parent: any) => {
	// 		const parentOrderIds = parent.orders.map((o: any) => o.id);

	// 		const allOrdersPresent = parentOrderIds.every((orderId: any) =>
	// 			acc.some((nk: any) => nk.id === orderId)
	// 		);

	// 		if (allOrdersPresent) {
	// 			// remove all matching children
	// 			return acc.filter((nk: any) => !parentOrderIds.includes(nk.id));
	// 		}

	// 		return acc;
	// 	}, subRows);

	// 	let rowsWhichParentNotSelectedIds = rowsWhichParentNotSelected.map((item: any) => item.id);
	// 	let selectedRowsIds = selectedRows.map((item: any) => item.id);
	// 	let totalRowsSelected = [...rowsWhichParentNotSelected, ...selectedRows]
	// 	let totalRowsSelectedIds = [...rowsWhichParentNotSelectedIds, ...selectedRowsIds]

	// 	return totalRowsSelected
	// }

	const selectedRowsWhichDontHaveChilds = selectedRows.filter((item: any) => item.orders.length === 0)
	const updatedTotalSelectedRowsWithChildsAndAlone = [...selectedRowsWhichDontHaveChilds, ...selectedSubRows]


	const onAmountCollectedButtonClickedHandler = () => {
		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("amount-collected")
			setDialogButtonLabel("Amount Collected")
			setDialogContent(`Are you sure you want to amount collected of (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to amount controlled!",
				severity: "error"
			})
		}
	}
	const onErrorInTransactionButtonClickedHandler = () => {
		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("error-in-transaction")
			setDialogButtonLabel("Error in Transaction")
			setDialogContent(`Are you sure you want to error in transaction of (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to error in transaction!",
				severity: "error"
			})
		}
	}
	const onDeleteButtonClickedHandler = () => {
		// const totalSelectedRows = updatedTotalSelectedRows(updatedRollBackTransactionsListItems, selectedSubRows)

		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("delete")
			setDialogButtonLabel("Delete")
			setDialogContent(`Are you sure you want to delete (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to delete!",
				severity: "error"
			})
		}
	}
	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	// const onCloseWireTransferWarningDialoageHandler = () => {
	// 	setOpenWireTransferWarningDialoage(false);
	// 	setOpenWireTransferWarningDialoageData(null)
	// 	setOpenWireTransferWarningDialoageDataForSave(null)
	// };

	// const onConfirmWireTransferWarningDialoageSubmit = () => {
	// 	console.log('openWireTransferWarningDialoageDataForSave', openWireTransferWarningDialoageDataForSave)
	// 	const updatePayloadData =
	// 		openWireTransferWarningDialoageDataForSave.map((item: any) => ({
	// 			...item,
	// 			lastTab: 900,
	// 			fromLastTabOn: 700,
	// 			// this line add after making clientname clickable 
	// 			clientName: item.clientName.props ? item.clientName.props.children : item.clientName
	// 		}));
	// 	processRollBackTransactions(updatePayloadData)
	// 	setOpenWireTransferWarningDialoage(false);
	// 	setOpenWireTransferWarningDialoageData(null)
	// 	setOpenWireTransferWarningDialoageDataForSave(null)
	// }
	const dialogButtonClickedHandler = async (tag: string | null) => {
		if (buttonTag !== null && buttonTag === 'amount-collected') {
			// const totalSelectedRows = updatedTotalSelectedRows(updatedRollBackTransactionsListItems, selectedSubRows)
			// processRollBackTransactions(selectedSubRows)
			const updatePayloadData =
				updatedTotalSelectedRowsWithChildsAndAlone.map((item: any) => ({
					// ...item,
					// lastTab: 900,
					// fromLastTabOn: 700,
					// clientName: item.clientName.props ? item.clientName.props.children : item.clientName,
					// employeeName: item.employeeName.props ? item.employeeName.props.children : item.employeeName,

					clientId: item.clientId,
					payrollSheduleId: item.payrollSheduleId,
					payPeriodEndDate: item.payPeriodEndDate,
					employeeId: item.employeeId,

				}));
			console.log('updatePayloadData', updatePayloadData);
			// confirmRollBackTransactions(updatePayloadData)
			(async () => {
				let payload = {
					transactionNextScreenTag: 100,
					data: updatePayloadData
				}
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.confirmRollBackTransactions(payload);
					// queryClient.invalidateQueries({ queryKey: [ROLL_BACK_TRANSACTIONS_QUERY_KEY] })
				} catch { } finally { setIsLoading(false), setDialogOpen(false) }
			})();



			// const totalWithWireMethods = updatePayloadData.filter(
			// 	(x: any) => x.transactionMethod === 200
			// 	// && Number(x.amount) > Number(x.accumulatedAmount)
			// );
			// console.log('totalWithWireMethods', totalWithWireMethods)
			// if (totalWithWireMethods && totalWithWireMethods.length) {

			// 	(async () => {
			// 		const wireIds = Array.from(new Set(totalWithWireMethods.map((x: any) => x.clientId)));
			// 		console.log('wireIds', wireIds)
			// 		try {
			// 			const response: any =
			// 				await TransactionsServices.getClientsAccumulatedAmounts(wireIds);

			// 			console.log('response', response)
			// 			if (response) {
			// 				// Step 1: Sum all amounts per clientId from items
			// 				const clientAmountMap = totalWithWireMethods.reduce((acc: any, item: any) => {
			// 					acc[item.clientId] = (acc[item.clientId] || 0) + item.amount;
			// 					return acc;
			// 				}, {});
			// 				// { "6a0b02877fd71cc0b1b4c547": 26185.43 }  (152.55 + 3518.34 + 22514.54)

			// 				// Step 2: Add availableAmount to data
			// 				const updatedData = response.map((d: any) => ({
			// 					...d,
			// 					availableAmount: clientAmountMap[d.clientId] || 0
			// 				}));

			// 				console.log('updatedItems', updatedData)

			// 				const acceptableWithWireMethods = updatedData.filter(
			// 					(x: any) => Number(x.availableAmount) > Number(x.accumulatedAmount)
			// 				);

			// 				if (acceptableWithWireMethods && acceptableWithWireMethods.length) {
			// 					const remainingItems = updatePayloadData.filter(
			// 						(x: any) => !acceptableWithWireMethods.some((wire: any) => wire.clientId === x.clientId)
			// 					);
			// 					const itemsWithWrongData = updatePayloadData.filter(
			// 						(x: any) => acceptableWithWireMethods.some((wire: any) => wire.clientId === x.clientId)
			// 					);

			// 					console.log('remainingItems', remainingItems)
			// 					console.log('remitemsWithWrongDataainingItems', itemsWithWrongData)
			// 					if (remainingItems && remainingItems.length) {
			// 						(async () => {
			// 							try {
			// 								const response: any =
			// 									await TransactionsServices.updateDirectDepositForCollections(remainingItems);
			// 								setDialogOpen(false)
			// 								setOpenWireTransferWarningDialoageData(acceptableWithWireMethods)
			// 								setOpenWireTransferWarningDialoageDataForSave(itemsWithWrongData)
			// 								setOpenWireTransferWarningDialoage(true)
			// 							} catch { }
			// 						})();
			// 					} else {
			// 						setDialogOpen(false)
			// 						setOpenWireTransferWarningDialoageData(acceptableWithWireMethods)
			// 						setOpenWireTransferWarningDialoageDataForSave(itemsWithWrongData)
			// 						setOpenWireTransferWarningDialoage(true)
			// 					}
			// 				}


			// 				// setDialogOpen(false)
			// 				// setOpenWireTransferWarningDialoageData(updatedItems)
			// 				// setOpenWireTransferWarningDialoage(true)
			// 			}
			// 		} catch { }
			// 	})();


			// 	// const remainingItems = updatePayloadData.filter(
			// 	// 	(x: any) => !totalWithWireMethods.some((wire: any) => wire.id === x.id)
			// 	// );
			// 	// console.log('remainingItems', remainingItems)

			// 	// if (remainingItems && remainingItems.length) {
			// 	// 	(async () => {
			// 	// 		try {
			// 	// 			const response: any =
			// 	// 				await TransactionsServices.updateDirectDepositForCollections(remainingItems);
			// 	// 			setDialogOpen(false)
			// 	// 			setOpenWireTransferWarningDialoageData(totalWithWireMethods)
			// 	// 			setOpenWireTransferWarningDialoage(true)
			// 	// 		} catch { }
			// 	// 	})();
			// 	// } else {
			// 	// 	setDialogOpen(false)
			// 	// 	setOpenWireTransferWarningDialoageData(totalWithWireMethods)
			// 	// 	setOpenWireTransferWarningDialoage(true)
			// 	// }
			// } else {
			// 	processRollBackTransactions(updatePayloadData)
			// 	setDialogOpen(false)
			// }
		} else if (buttonTag !== null && buttonTag === 'delete') {
			// const totalSelectedRows = updatedTotalSelectedRows(updatedRollBackTransactionsListItems, selectedSubRows);
			(async () => {
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.deleteReadyTransactions(selectedSubRows);
					queryClient.invalidateQueries({ queryKey: [ROLL_BACK_TRANSACTIONS_QUERY_KEY] })
				} catch { } finally { setIsLoading(false) }
			})();
		} else if (tag === 'error-in-transaction') {
			// const totalSelectedRows = updatedTotalSelectedRows(upadtedInCollectionTransactionsListItems, selectedRows);
			// (async () => {
			// 	try {
			// 		setIsLoading(true)
			// 		const response: any =
			// 			await TransactionsServices.errorInTransactionInCollectionTransactions(selectedRows);
			// 		queryClient.invalidateQueries({ queryKey: [IN_COLLECTION_TRANSACTIONS_QUERY_KEY] })
			// 	} catch { } finally { setIsLoading(false) }
			// })();

			const updatePayloadData =
				selectedSubRows.map((item: any) => ({
					// ...item,
					// transactionErrorId: item.errorMessage,
					// errorMessage: errorMessagesDDL && errorMessagesDDL.find((x: any) => x.value === item.errorMessage)?.label,
					// lastTab: 600,
					// fromLastTabOn: 1300,
					// this line add after making clientname clickable 
					// clientName: item.clientName.props ? item.clientName.props.children : item.clientName,
					// employeeName: item.employeeName.props ? item.employeeName.props.children : item.employeeName,

					clientId: item.clientId,
					payrollSheduleId: item.payrollSheduleId,
					payPeriodEndDate: item.payPeriodEndDate,
					employeeId: item.employeeId,
				}));
			// saveRollBackTransactions(updatePayloadData)
			(async () => {
				let payload = {
					transactionNextScreenTag: 200,
					data: updatePayloadData
				}
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.confirmRollBackTransactions(payload);
					queryClient.invalidateQueries({ queryKey: [ROLL_BACK_TRANSACTIONS_QUERY_KEY] })
				} catch { } finally { setIsLoading(false) }
				setDialogOpen(false)
			})();
		}
	}
	const expandedLabels = ["Transactions", "Rollback Transactions"];
	const expandedRollBackTransactionSetupItem = "Rollback Transactions";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Rollback Transactions" &&
				item.label === expandedRollBackTransactionSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedRollBackTransactionSetupItem && "/direct-deposit/transactions/rollback",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);



	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Rollback Transactions"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<RollBackTransactionsHeader
						clientsDDL={clientsDDLItems}
						accountantDDL={accountantDDLItems}
						employeeDDL={employeeDDLItems}
						onAccountantSelectionChangeHandler={onAccountantSelectionChangeHandler}
						onClientSelectionChangeHandler={onClientSelectionChangeHandler}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)} />
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isLoading ||
						isPendingClientsDDLItems ||
						isProcessing || isPendingAccountantDDLItemsItems
					}
					testId="roll-back-transactions"
				/>
				<RollBackTransactionsDataGrid
					columns={
						rollBackTransactionsData?.headerColumns
							? rollBackTransactionsData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
					}
					rows={
						rollBackTransactionsData && updatedRollBackTransactionsListItems
							? updatedRollBackTransactionsListItems
							: []
						// rollBackTransactionsData && rollBackTransactionsData.items
						// 	? rollBackTransactionsData.items
						// 	: []
					}
					showIndexColumn={true}
					showChecboxColumn={true}
					showSettingColumn={false}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						rollBackTransactionsData && rollBackTransactionsData.total
							? rollBackTransactionsData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search..."}
					showRowExpandedView={true}
					selectedRows={(value: any) => setSelectedRows(value)}
					selectedSubRows={(value: any) => setSelectedSubRows(value)}
				/>
				{rollBackTransactionsData && rollBackTransactionsData.items && rollBackTransactionsData.items.length > 0 && (
					<RollBackTransactionsToolbar
						onAmountCollectedButtonClickedHandler={onAmountCollectedButtonClickedHandler}
						onErrorInTransactionButtonClickedHandler={onErrorInTransactionButtonClickedHandler}
					/>
				)}
			</Grid>

			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseHandler}
				open={openDialog}
				dialogContent={dialogContent}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label={dialogButtonLabel}
							severity={buttonTag === 'amount-collected' ? EnhancedButtonPrimeReactSeverity.Success : EnhancedButtonPrimeReactSeverity.Secondary}
							raised
							onClickHandler={() => dialogButtonClickedHandler(buttonTag)}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>



			{/* Wire Transfer Warning Dialoage */}
			{/* <EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseWireTransferWarningDialoageHandler}
				open={openWireTransferWarningDialoage}
				dialogContent={
					<Box>
						<Typography variant="body2" sx={{ mb: 2 }}>
							Total amount of wire transfer companies exceed available balance. <br />
							Click Ok to proceed transactions
						</Typography>
						{openWireTransferWarningDialoageData && openWireTransferWarningDialoageData.map((item: any) => (
							<Typography variant="body2" sx={{ mb: 2 }}>
								{item.clientName} <br />
								<Box sx={{ display: "flex", justifyContent: "space-between" }}>
									<Typography sx={{ fontSize: ".875rem" }}>Selected Transactions Amount: <span style={{ fontWeight: "bold" }}>{formatAmountCommaSeperator(item.availableAmount)}</span></Typography>
									<Typography sx={{ fontSize: ".875rem" }}>Available: <span style={{ fontWeight: "bold" }}>{formatAmountCommaSeperator(item.accumulatedAmount)}</span></Typography>
								</Box>
								Difference: <span style={{ fontWeight: "bold" }}>{formatAmountCommaSeperator(item.availableAmount - item.accumulatedAmount)}</span>
							</Typography>
						))}
					</Box>
				}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseWireTransferWarningDialoageHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Ok"
							severity="success"
							raised
							onClickHandler={onConfirmWireTransferWarningDialoageSubmit}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/> */}

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
